import { useEffect, useState } from 'react'
import { APIErrors } from '../../../api-client/enums'
import { useAuth } from '../../../contexts/auth'
import useValidation from '../../../hooks/use-validation'
import { Button } from '../../../components/button'
import { FormField } from '../../../components/inputs'

const re = {
  email: /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i,
}

const validationErrors = {
  notValid: 'Email is not valid.',
  notAvailable: 'Email is already in use.',
  required: 'Required',
  invalidGrant: 'Invalid Password',
}

export default function UpdateEmailRequest({ onSubmit, emailMutationInProgress, serverErrors }) {
  const [{ userDetails }] = useAuth()
  const [state, setState] = useState({
    newEmail: '',
    password: '',
  })
  const [mappedServerErrors, setMappedServerErrors] = useState({})

  const [showErrors, { isFormValid, errors }] = useValidation(state, validate)

  useEffect(() => setMappedServerErrors(mapServerErrors(serverErrors?.errors)), [serverErrors])

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!isFormValid) showErrors()
    else onSubmit({ email: state.newEmail, password: state.password })
  }

  const handleInputChange = (e) => {
    e.persist()

    setState((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  return (
    <form className='wrapper' onSubmit={handleSubmit}>
      <h1 className='title'>Update your Email Address</h1>
      <div className='inputFieldWrapper currentEmail'>
        <FormField type='email' name='currentEmail' value={userDetails.email} disabled={true} />
      </div>
      <div className='inputFieldWrapper newEmail'>
        {(errors.newEmail || mappedServerErrors.newEmail) && (
          <span className='errorLabel'>{errors.newEmail || mappedServerErrors.newEmail}</span>
        )}
        <FormField
          type='email'
          inputMode='email'
          name='newEmail'
          error={errors.newEmail || mappedServerErrors.newEmail}
          placeholder='New Email Address'
          autoComplete='email'
          onChange={handleInputChange}
        />
      </div>
      <div className='inputFieldWrapper password'>
        {(errors.password || mappedServerErrors.password) && (
          <span className='errorLabel'>{errors.password || mappedServerErrors.password}</span>
        )}
        <FormField
          type='password'
          name='password'
          error={errors.password || mappedServerErrors.password}
          placeholder='Confirm Password'
          autoComplete='password'
          onChange={handleInputChange}
        />
      </div>

      {mappedServerErrors[APIErrors.Unknown] && (
        <p className='errorText'>{mappedServerErrors[APIErrors.Unknown].message}</p>
      )}

      <Button className='saveBtn' type='submit' disabled={emailMutationInProgress}>
        Save
      </Button>

      <style jsx>{`
        .wrapper {
          position: relative;
          width: 43.6rem;
          padding: 2.6rem 1.8rem 4.1rem;
          background: #fff;
          border-radius: 1rem;

          :global(.saveBtn) {
            display: block;
            margin: 0 auto;
          }
        }

        .inputFieldWrapper {
          &.currentEmail {
            margin: 0 0 3rem;
          }

          &.newEmail {
            margin: 0 0 1.7rem;
          }

          &.password {
            margin: 0 0 3.8rem;
          }
        }

        .title {
          margin: 0 0 2.8rem;
          text-align: center;
          font-size: 3.2rem;
        }

        .inputFieldWrapper {
          position: relative;
        }

        .errorLabel {
          display: block;
          margin: 0 0 1rem;
          color: var(--alert-color);
          font-size: 1.4rem;
        }

        .errorText {
          padding: 0 0 2.7rem;
          color: var(--alert-color);
          line-height: 2.1rem;
          font-size: 1.6rem;
        }

        @media (max-width: 1024px) {
          .wrapper {
            width: 100%;
            height: 100%;
            padding: 5rem 1.4rem 2.3rem;

            :global(.saveBtn) {
              position: fixed;
              bottom: 2.1rem;
              left: 1.2rem;
              width: calc(100vw - 2.4rem);
            }
          }

          .title {
            margin: 0 0 3rem;
            text-align: center;
            font-size: 1.8rem;
          }
        }
      `}</style>
    </form>
  )
}

function validate(state) {
  const errors = {}
  let isValid = true

  if (!re.email.test(state.newEmail)) errors['newEmail'] = validationErrors.notValid
  if (state.password === '') errors['password'] = validationErrors.required

  if (Object.keys(errors).length > 0) isValid = false

  return { isValid, errors }
}

function mapServerErrors(errors) {
  if (!errors) return {}

  const mappedErrors = {}

  errors.forEach((error) => {
    switch (+error?.code) {
      case APIErrors.EmailAlreadyTaken:
        mappedErrors['newEmail'] = validationErrors.notAvailable
        break
      case APIErrors.InvalidGrant:
        mappedErrors['password'] = validationErrors.invalidGrant
        break
      default:
        mappedErrors[APIErrors.Unknown] = error.message
        break
    }
  })

  return mappedErrors
}
